// Content for CMED.AI marketing site (bilingual EN/AR, frontend-only)

export const bookingUrl = "https://calendly.com/cmed-info/30min";

export const brand = {
  name: "CMED.AI",
  email: "info@cmed.ai",
  logo: "/cmed-logo.png",
};

// Structural (non-text) data shared across languages
export const navHrefs = ["#home", "#about", "#how", "#app", "#contact"];

export const media = {
  heroImage: "/qatari-hero.jpg?v=2",
  aboutImage: "/about-qatari.png",
  whoUsesImage: "https://images.pexels.com/photos/7951932/pexels-photo-7951932.jpeg?auto=compress&cs=tinysrgb&w=1200",
  localNetworkImage: "/clinic-doha.png",
  appScreens: [
    "https://customer-assets-39nsmqrw.emergentagent.net/job_cmed-improve/artifacts/14lde0fp_WhatsApp%20Image%202026-09-10%20at%2017.40.52.webp",
    "https://customer-assets-39nsmqrw.emergentagent.net/job_cmed-improve/artifacts/l7fr5rvm_WhatsApp%20Image%202026-09-10%20at%2017.40.53.webp",
  ],
  promoPoster: null,
  promoVideo: "https://customer-assets-39nsmqrw.emergentagent.net/job_cmed-improve/artifacts/ysvo4y8s_CMED%20Teaser%202.mp4",
  // Video testimonials. caption is editable, intentionally empty for now.
  testimonials: [
    { videoUrl: "https://customer-assets-39nsmqrw.emergentagent.net/job_cmed-improve/artifacts/d8qhevd6_IMG_9206.MOV", poster: null, caption: "" },
    { videoUrl: "https://customer-assets-39nsmqrw.emergentagent.net/job_cmed-improve/artifacts/eqxy9q3z_0905.MOV", poster: null, caption: "" },
  ],
};

export const icons = {
  aboutBenefits: ["Activity", "Route", "MapPin", "Landmark"],
  whoUses: ["Plane", "Users", "Building2", "ShieldCheck", "Briefcase"],
  services: ["Compass", "MapPin", "Route"],
  care: ["Pill", "Stethoscope", "HeartPulse", "Ambulance"],
  how: [{ icon: "MessageCircle", step: "1" }, { icon: "Cpu", step: "2" }, { icon: "ClipboardCheck", step: "3" }],
};

export const translations = {
  en: {
    dir: "ltr",
    toggleLabel: "العربية",
    cta: "Request a demo",
    emergency: "Not for emergencies. In an emergency, call 999.",
    nav: ["Home", "About", "How it works", "App", "Contact"],
    hero: {
      eyebrow: "Intelligent healthcare navigation",
      traction: "Live pilot in Qatar",
      title: "Smarter care for everyone",
      subtitle: "CMED.AI makes healthcare accessible, intelligent, and focused on people. We provide faster answers on where to go, guiding you to the right local health services and explaining what government and Clinics and Hospitals offer.",
      disclaimer: "CMED.AI guides you to the right care. It does not diagnose.",
      secondaryCta: "See how it works",
      locationNote: "Built for patients & clinics across Qatar",
      badgeTitle: "Care in 60 seconds",
      badgeText: "Guided and focused on safety",
    },
    stats: [
      { value: "24/7", label: "Symptom check." },
      { value: "Qatar", label: "Pilot is live" },
      { value: "Public & Private", label: "Clinics & Hospitals" },
    ],
    about: {
      eyebrow: "About us",
      title: "Building a smarter, healthier world",
      body: [
        "CMED.AI is transforming the way people reach medical care. Our platform helps patients understand their symptoms, find the right care across pharmacy, primary care, specialist and emergency services, and navigate local health services with confidence.",
      ],
      founder: "Every clinical pathway is reviewed and signed off by our clinical assurance team.",
      badgeTitle: "Care first",
      badgeText: "Technology meets compassion",
      benefits: [
        { title: "Quick Symptom Check", text: "Describe how you feel and receive clear guidance in under a minute." },
        { title: "Personalised Care Navigation", text: "We direct you to the right level of care for your needs, every time." },
        { title: "Clear Guidance to Local Services", text: "Find the most suitable nearby health service in Qatar quickly." },
        { title: "Government and Private Providers", text: "Understand what each provider offers so you can choose with confidence." },
      ],
    },
    how: {
      eyebrow: "How it works",
      title: "From symptom to the right care in three steps",
      subtitle: "A guided conversation that ends with a clear, safe recommendation, including practical steps such as your Qatar Health Card.",
      steps: [
        { title: "Describe your symptoms", text: "Speak naturally with the CMED assistant. It asks relevant questions about your symptoms, history, and any warning signs." },
        { title: "Intelligent assessment", text: "Our clinically informed system weighs your answers to determine the safest and most appropriate level of care." },
        { title: "Receive your care plan", text: "Get clear guidance across pharmacy, primary care, specialist or emergency services, with what to do next and where to go." },
      ],
    },
    services: {
      eyebrow: "Our services",
      title: "Smarter solutions for better care",
      items: [
        { title: "Personalised Care Guidance", text: "CMED.AI reviews your symptoms to guide you to the most appropriate care, saving time, reducing uncertainty, and helping you make informed decisions about your health." },
        { title: "The Right Care Provider", text: "Whether you need a nearby pharmacy, a general practitioner, or specialist or emergency care, CMED.AI directs you to the right provider for your needs and location." },
        { title: "Local Health Navigation", text: "CMED.AI explains what local services offer, across government and Clinics and Hospitals, and assists with practical steps such as understanding your options or renewing your Health Card." },
      ],
    },
    careLevels: { title: "One clear path across every level of care", steps: ["Pharmacy", "Primary Care", "Specialist", "Emergency"] },
    localNetwork: {
      eyebrow: "Local health network",
      title: "Connected to Qatar's modern health network",
      subtitle: "From primary health centres to leading private hospitals, CMED.AI helps you understand your options across government and private clinics and hospitals, so you always know where to go.",
      points: ["Government and private clinics and hospitals, explained clearly", "Guidance to your nearest, most suitable service", "Practical steps, such as renewing your Health Card"],
    },
    appShowcase: {
      eyebrow: "Inside the app",
      title: "A calm, guided experience built for real health decisions",
      subtitle: "A first look at the CMED.AI experience, from your first question to clear, guided next steps.",
      screens: ["Start in seconds", "Guided conversation"],
      note: "This is just a glimpse. Request a demo to see the full experience.",
    },
    promo: {
      eyebrow: "Watch the story",
      title: "See how CMED.AI helps you find the right care",
      subtitle: "A short introduction to how CMED.AI guides people across Qatar to the right care, clearly and in their own language.",
    },
    testimonials: {
      eyebrow: "Stories from our community",
      title: "Real people, real peace of mind",
      subtitle: "Hear how CMED.AI helped people across Qatar find the right care, faster.",
    },
    whoUses: {
      eyebrow: "Who uses CMED.AI",
      title: "Care that works for everyone in Qatar",
      intro: "From Qatari nationals to the many expat communities who call Qatar home, CMED.AI offers everyone clear and local guidance.",
      items: [
        { title: "Expat Populations", text: "Navigate an unfamiliar health system with clear, local guidance." },
        { title: "Local Populations", text: "Fast answers on where to go for the right care, close to home." },
        { title: "Hospitals & Clinics", text: "Intelligent routing that sends patients to the right service." },
        { title: "Insurance & Health Partners", text: "Guidance informed by data and aligned with national health goals." },
        { title: "Employers & Corporate Health", text: "Support staff wellbeing with accessible care guidance." },
      ],
    },
    contact: {
      eyebrow: "Contact us",
      title: "Get in touch",
      subtitle: "We are building the future of healthcare in Qatar, and we would be glad to hear from you.",
      address: "Doha, Qatar",
      labelAddress: "Address",
      labelEmail: "Email",
      form: { first: "First name", last: "Last name", email: "Email", phone: "Phone", subject: "Subject", message: "Message", send: "Send message", sending: "Sending...", ok: "Thanks! We\u2019ll be in touch shortly.", err: "Please fill in your name, email and message." },
    },
    footer: {
      blurb: "Intelligent healthcare navigation for Qatar, providing faster clarity on where to go and guidance across government and Clinics and Hospitals.",
      address: "Doha, Qatar",
      columns: [
        { title: "Product", links: ["Symptom Check", "Care Navigation", "Local Services", "Government & Private"] },
        { title: "Company", links: ["About", "How it works", "Careers", "Contact"] },
        { title: "Legal", links: ["Privacy", "Terms", "Medical Disclaimer"] },
      ],
      rights: "All rights reserved.",
      disclaimer: "CMED.AI is a care navigation tool and does not diagnose or replace professional medical advice.",
    },
  },
  ar: {
    dir: "rtl",
    toggleLabel: "English",
    cta: "اطلب عرضاً توضيحياً",
    emergency: "ليست للطوارئ. في حالة الطوارئ، اتصل بالرقم 999.",
    nav: ["الرئيسية", "من نحن", "كيف يعمل", "التطبيق", "تواصل معنا"],
    hero: {
      eyebrow: "التوجيه الذكي للرعاية الصحية",
      traction: "تجربة حية في قطر",
      title: "رعاية أذكى للجميع",
      subtitle: "يجعل CMED.AI الرعاية الصحية متاحة وذكية وموجّهة للإنسان. نقدّم إجابات أسرع حول الوجهة المناسبة، ونرشدك إلى الخدمات الصحية المحلية الصحيحة، ونوضّح ما تقدّمه الجهات الحكومية والخاصة.",
      disclaimer: "يرشدك CMED.AI إلى الرعاية المناسبة، ولا يقوم بالتشخيص.",
      secondaryCta: "شاهد كيف يعمل",
      locationNote: "مصمم للمرضى والعيادات في جميع أنحاء قطر",
      badgeTitle: "رعاية خلال 60 ثانية",
      badgeText: "موجّهة وتركّز على السلامة",
    },
    stats: [
      { value: "24/7", label: "فحص الأعراض." },
      { value: "قطر", label: "التجربة جارية" },
      { value: "حكومي وخاص", label: "عيادات ومستشفيات" },
    ],
    about: {
      eyebrow: "من نحن",
      title: "نبني عالماً أكثر ذكاءً وصحة",
      body: [
        "يعمل CMED.AI على تغيير الطريقة التي يصل بها الناس إلى الرعاية الطبية. تساعد منصتنا المرضى على فهم أعراضهم، وإيجاد الرعاية المناسبة عبر الصيدلية والرعاية الأولية والاختصاصي وخدمات الطوارئ، والتنقّل بين الخدمات الصحية المحلية بثقة.",
      ],
      founder: "تتم مراجعة كل مسار سريري واعتماده من قبل فريق الضمان السريري لدينا.",
      badgeTitle: "الرعاية أولاً",
      badgeText: "التقنية تلتقي بالرحمة",
      benefits: [
        { title: "فحص سريع للأعراض", text: "صِف شعورك واحصل على إرشاد واضح في أقل من دقيقة." },
        { title: "توجيه رعاية مخصّص", text: "نوجّهك إلى المستوى المناسب من الرعاية لاحتياجاتك، في كل مرة." },
        { title: "إرشاد واضح للخدمات المحلية", text: "اعثر بسرعة على أنسب خدمة صحية قريبة في قطر." },
        { title: "مقدّمو الخدمات الحكوميون والخاصون", text: "افهم ما يقدّمه كل مقدّم خدمة لتختار بثقة." },
      ],
    },
    how: {
      eyebrow: "كيف يعمل",
      title: "من العرض إلى الرعاية المناسبة في ثلاث خطوات",
      subtitle: "محادثة موجّهة تنتهي بتوصية واضحة وآمنة، بما في ذلك خطوات عملية مثل بطاقتك الصحية القطرية.",
      steps: [
        { title: "صِف أعراضك", text: "تحدّث بشكل طبيعي مع مساعد CMED. يطرح أسئلة مناسبة عن أعراضك وتاريخك الطبي وأي علامات تحذيرية." },
        { title: "تقييم ذكي", text: "يقوم نظامنا المبني على أسس سريرية بتقييم إجاباتك لتحديد المستوى الأنسب والأكثر أماناً من الرعاية." },
        { title: "استلم خطة رعايتك", text: "احصل على إرشادات واضحة عبر الصيدلية والرعاية الأولية والاختصاصي أو خدمات الطوارئ، مع ما يجب فعله لاحقاً وأين تذهب." },
      ],
    },
    services: {
      eyebrow: "خدماتنا",
      title: "حلول أذكى لرعاية أفضل",
      items: [
        { title: "توجيه رعاية مخصّص", text: "يراجع CMED.AI أعراضك ليوجّهك إلى الرعاية الأنسب، موفّراً الوقت ومقلّلاً الحيرة ومساعداً إياك على اتخاذ قرارات مدروسة بشأن صحتك." },
        { title: "مقدّم الرعاية المناسب", text: "سواء احتجت إلى صيدلية قريبة أو طبيب عام أو اختصاصي أو رعاية طارئة، يوجّهك CMED.AI إلى المقدّم المناسب لاحتياجاتك وموقعك." },
        { title: "التنقّل الصحي المحلي", text: "يوضّح CMED.AI ما تقدّمه الخدمات المحلية عبر المقدّمين الحكوميين والخاصين، ويساعد في خطوات عملية مثل فهم خياراتك أو تجديد بطاقتك الصحية." },
      ],
    },
    careLevels: { title: "مسار واحد واضح عبر كل مستويات الرعاية", steps: ["الصيدلية", "الرعاية الأولية", "الاختصاصي", "الطوارئ"] },
    localNetwork: {
      eyebrow: "الشبكة الصحية المحلية",
      title: "متصل بشبكة قطر الصحية الحديثة",
      subtitle: "من مراكز الرعاية الصحية الأولية إلى كبرى المستشفيات الخاصة، يساعدك CMED.AI على فهم خياراتك عبر العيادات والمستشفيات الحكومية والخاصة، لتعرف دائماً إلى أين تذهب.",
      points: ["عيادات ومستشفيات حكومية وخاصة، بشرح واضح", "إرشاد إلى أقرب خدمة وأنسبها", "خطوات عملية، مثل تجديد بطاقتك الصحية"],
    },
    appShowcase: {
      eyebrow: "داخل التطبيق",
      title: "تجربة هادئة وموجّهة مصمّمة لقرارات صحية حقيقية",
      subtitle: "لمحة أولى عن تجربة CMED.AI، من سؤالك الأول إلى خطوات تالية واضحة وموجّهة.",
      screens: ["ابدأ خلال ثوانٍ", "محادثة موجّهة"],
      note: "هذه مجرد لمحة. اطلب عرضاً توضيحياً لرؤية التجربة كاملة.",
    },
    promo: {
      eyebrow: "شاهد القصة",
      title: "شاهد كيف يساعدك CMED.AI في إيجاد الرعاية المناسبة",
      subtitle: "مقدّمة قصيرة عن كيفية إرشاد CMED.AI للناس في قطر إلى الرعاية المناسبة، بوضوح وبلغتهم.",
    },
    testimonials: {
      eyebrow: "قصص من مجتمعنا",
      title: "أشخاص حقيقيون، وراحة بال حقيقية",
      subtitle: "استمع إلى كيف ساعد CMED.AI الناس في قطر على إيجاد الرعاية المناسبة بشكل أسرع.",
    },
    whoUses: {
      eyebrow: "من يستخدم CMED.AI",
      title: "رعاية تناسب الجميع في قطر",
      intro: "من المواطنين القطريين إلى العديد من جاليات الوافدين التي تتخذ من قطر وطناً، يقدّم CMED.AI للجميع إرشاداً واضحاً ومحلياً.",
      items: [
        { title: "الوافدون", text: "تنقّل في نظام صحي غير مألوف بإرشاد واضح ومحلي." },
        { title: "السكان المحليون", text: "إجابات سريعة حول الوجهة المناسبة للرعاية، قريباً من المنزل." },
        { title: "المستشفيات والعيادات", text: "توجيه ذكي يرسل المرضى إلى الخدمة المناسبة." },
        { title: "شركاء التأمين والصحة", text: "إرشاد مبني على البيانات ومتوافق مع الأهداف الصحية الوطنية." },
        { title: "أصحاب العمل والصحة المؤسسية", text: "دعم صحة الموظفين بإرشاد رعاية سهل الوصول." },
      ],
    },
    contact: {
      eyebrow: "تواصل معنا",
      title: "ابقَ على تواصل",
      subtitle: "نحن نبني مستقبل الرعاية الصحية في قطر، ويسعدنا أن نسمع منك.",
      address: "الدوحة، قطر",
      labelAddress: "العنوان",
      labelEmail: "البريد الإلكتروني",
      form: { first: "الاسم الأول", last: "اسم العائلة", email: "البريد الإلكتروني", phone: "الهاتف", subject: "الموضوع", message: "الرسالة", send: "إرسال الرسالة", sending: "جارٍ الإرسال...", ok: "شكراً! سنتواصل معك قريباً.", err: "يرجى إدخال اسمك وبريدك الإلكتروني ورسالتك." },
    },
    footer: {
      blurb: "توجيه ذكي للرعاية الصحية في قطر، يوفّر وضوحاً أسرع حول الوجهة المناسبة وإرشاداً عبر المقدّمين الحكوميين والخاصين.",
      address: "الدوحة، قطر",
      columns: [
        { title: "المنتج", links: ["فحص الأعراض", "توجيه الرعاية", "الخدمات المحلية", "حكومي وخاص"] },
        { title: "الشركة", links: ["من نحن", "كيف يعمل", "الوظائف", "تواصل معنا"] },
        { title: "قانوني", links: ["الخصوصية", "الشروط", "إخلاء المسؤولية الطبية"] },
      ],
      rights: "جميع الحقوق محفوظة.",
      disclaimer: "CMED.AI أداة لتوجيه الرعاية ولا تُشخّص أو تحل محل المشورة الطبية المتخصصة.",
    },
  },
};
